import React from 'react'
import { AxiosRequestConfig } from 'axios'
import { axiosInstance, useAxios } from '../hooks/useAxios'
import Card from './Card'
import classNames from 'classnames'
import Skeleton from 'react-loading-skeleton'
import InformationItem from './InformationItem'
import { toast } from 'react-toastify'
import Btn from './Btn'
import { ReactSVG } from 'react-svg'
import { FieldValues, useForm, UseFormReturn } from 'react-hook-form'

interface Props {
  listMethod: () => AxiosRequestConfig
  deleteMethod: (id: string) => AxiosRequestConfig
  addMethod: (data: any) => AxiosRequestConfig
  updateMethod: (id: string, data: any) => AxiosRequestConfig
  deleteSuccessText?: string
  addSuccessText?: string
  updateSuccessText?: string
  preload?: () => Promise<void>
  renderView: (item: any) => React.ReactElement | string
  renderUpdate: (
    form: UseFormReturn<FieldValues, any>
  ) => React.ReactElement | string
  emptyText?: string
  addButtonText?: string
  addTitleText?: string
  renderAdd: (
    form: UseFormReturn<FieldValues, any>
  ) => React.ReactElement | string
}

const InformationItems: React.FC<Props> = ({
  listMethod,
  preload,
  renderView,
  deleteMethod,
  deleteSuccessText = 'Успешно',
  emptyText = 'Нет данных',
  renderAdd,
  addButtonText = 'Добавить',
  addTitleText = 'Добавить',
  addMethod,
  addSuccessText = 'Успешно',
  updateMethod,
  updateSuccessText = 'Успешно',
  renderUpdate
}) => {
  const { error, fire } = useAxios()
  const [loading, setLoading] = React.useState<boolean>(true)
  const [items, setItems] = React.useState<any>([])
  const [addMode, setAddMode] = React.useState<boolean>(false)
  const form = useForm()

  React.useEffect(() => {
    setLoading(true)
    const prom = fire(listMethod()).then(({ response }) => {
      setItems(response?.data ?? [])
    })

    void Promise.all([prom, preload?.()]).finally(() => setLoading(false))
  }, [])

  const deleteHandler = async (id: string) => {
    try {
      await axiosInstance(deleteMethod(id))

      setItems(items.filter((item: any) => item.id !== id))
      toast.success(deleteSuccessText)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  const { fire: submitFire, loading: submitLoading } = useAxios()

  const onSubmit = async (data: any) => {
    const { response, error } = await submitFire(addMethod(data))

    if (error) {
      toast.error(error.message)
    } else {
      setAddMode(false)
      form.reset()
      setItems([...items, response?.data])
      toast.success(addSuccessText)
    }
  }

  const onUpdate = (newItem: any) => {
    const index = items.findIndex(({ id }: any) => newItem.id === id)

    if (index !== -1) {
      setItems([...items.slice(0, index), newItem, ...items.slice(index + 1)])
    }
  }

  return loading ? (
    <>
      {Array.from({ length: 2 }).map((_, index) => (
        <Card
          key={index}
          className={classNames({ 'mt-md-5 mt-sm-4': !!index })}
        >
          <header className={'card-head d-block'}>
            <Skeleton width={'25%'}></Skeleton>
          </header>

          <div className={'card-content'}>
            <Skeleton count={2}></Skeleton>
          </div>
        </Card>
      ))}
    </>
  ) : !error ? (
    <div>
      {items.length ? (
        <>
          {' '}
          {items.map((item: any, index: number) => {
            return (
              <InformationItem
                renderUpdate={renderUpdate}
                updateMethod={updateMethod}
                updateSuccessText={updateSuccessText}
                onDelete={deleteHandler}
                className={classNames({ 'mt-md-5 mt-sm-4': !!index })}
                key={item.id}
                renderView={renderView}
                item={item}
                onUpdate={onUpdate}
              ></InformationItem>
            )
          })}
        </>
      ) : (
        <div className={'text-body-small text-secondary-color'}>
          {emptyText}
        </div>
      )}
      {!addMode ? (
        <div className={'mt-md-8 mt-sm-6'}>
          <Btn
            onClick={() => {
              setAddMode(true)
            }}
            text
            small
          >
            <span>{addButtonText}</span>
            <ReactSVG wrapper={'span'} src={'/i/svg/plus.svg'}></ReactSVG>
          </Btn>
        </div>
      ) : (
        <Card
          className={classNames({
            'mt-md-5 mt-sm-4': items.length,
            'mt-md-8 mt-sm-6': !items.length
          })}
        >
          <header className={'card-head text-h3'}>{addTitleText}</header>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className={'card-content'}
          >
            {renderAdd(form)}

            <div className={'mt-md-8 mt-sm-6 u-flex u-flexAlignItemsCenter'}>
              <Btn
                loading={submitLoading}
                disabled={submitLoading}
                type={'submit'}
              >
                Добавить
              </Btn>
              <Btn
                className={'ml-md-5 ml-sm-4'}
                outlined
                onClick={() => {
                  setAddMode(false)
                  form.reset()
                }}
              >
                Удалить
              </Btn>
            </div>
          </form>
        </Card>
      )}
    </div>
  ) : (
    error.message
  )
}

export default InformationItems
